/**
 * Kanonisk lista över alla viltslag i Qvenari.
 * Denna fil är den enda sanningskällan — Erbjud Jakt, Avskjutningsplan och Skjutmeny
 * importerar alla från hit.
 */

export type SpKey =
  'alg' | 'kronhjort' | 'dovhjort' | 'radjur' | 'vildsvin' | 'mufflon' |
  'rav' | 'gravling' | 'hare' | 'bjorn' |
  'fasan' | 'orre' | 'tjader' | 'skogsfagel' |
  'kanadagas' | 'andfagel' |
  'baver' | 'mink' | 'mardhund';

export interface SubcatDef {
  key: string; label: string; pctOfHarvest: number; fallKrDefault: number; slaktKg: number;
}
export interface SpeciesDef {
  key: SpKey; label: string; refPer100Ha: number; harvFrac: number;
  fallKrDefault: number; meatKgDefault: number;
}

export const SPECIES: SpeciesDef[] = [
  // Klövvilt
  { key: 'alg',        label: 'Älg',        refPer100Ha: 1.15, harvFrac: 0.12, fallKrDefault: 3200, meatKgDefault: 75 },
  { key: 'kronhjort',  label: 'Kronhjort',  refPer100Ha: 2.8,  harvFrac: 0.15, fallKrDefault: 4500, meatKgDefault: 80 },
  { key: 'dovhjort',   label: 'Dovhjort',   refPer100Ha: 5.5,  harvFrac: 0.18, fallKrDefault: 2800, meatKgDefault: 75 },
  { key: 'radjur',     label: 'Rådjur',     refPer100Ha: 22,   harvFrac: 0.22, fallKrDefault: 600,  meatKgDefault: 90 },
  { key: 'vildsvin',   label: 'Vildsvin',   refPer100Ha: 12,   harvFrac: 0.35, fallKrDefault: 400,  meatKgDefault: 65 },
  { key: 'mufflon',    label: 'Mufflon',    refPer100Ha: 3,    harvFrac: 0.15, fallKrDefault: 1800, meatKgDefault: 70 },
  // Rovvilt / allätare
  { key: 'rav',        label: 'Räv',        refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'gravling',   label: 'Grävling',   refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'hare',       label: 'Hare',       refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'bjorn',      label: 'Björn',      refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  // Fågel
  { key: 'fasan',      label: 'Fasan',      refPer100Ha: 35,   harvFrac: 0.28, fallKrDefault: 150,  meatKgDefault: 0  },
  { key: 'orre',       label: 'Orre',       refPer100Ha: 18,   harvFrac: 0.20, fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'tjader',     label: 'Tjäder',     refPer100Ha: 10,   harvFrac: 0.15, fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'skogsfagel', label: 'Skogsfågel', refPer100Ha: 18,   harvFrac: 0.20, fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'kanadagas',  label: 'Kanadagås',  refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'andfagel',   label: 'Andfågel',   refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  // Småvilt / invasivt
  { key: 'baver',      label: 'Bäver',      refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'mink',       label: 'Mink',       refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
  { key: 'mardhund',   label: 'Mårdhund',   refPer100Ha: 0,    harvFrac: 1.0,  fallKrDefault: 0,    meatKgDefault: 0  },
];

export const SUBCATS: Partial<Record<SpKey, SubcatDef[]>> = {
  alg: [
    { key: 'alg_tjur_stor', label: 'Tjur >10 taggar',     pctOfHarvest: 0.15, fallKrDefault: 6500, slaktKg: 180 },
    { key: 'alg_tjur_4_10', label: 'Tjur 4–10 taggar',    pctOfHarvest: 0.20, fallKrDefault: 4500, slaktKg: 150 },
    { key: 'alg_tjur_2_4',  label: 'Tjur 2–4 taggar',     pctOfHarvest: 0.15, fallKrDefault: 3000, slaktKg: 110 },
    { key: 'alg_kviga',     label: 'Kviga',                pctOfHarvest: 0.10, fallKrDefault: 2200, slaktKg: 90  },
    { key: 'alg_ko',        label: 'Ko',                   pctOfHarvest: 0.25, fallKrDefault: 3000, slaktKg: 120 },
    { key: 'alg_kalv',      label: 'Kalv',                 pctOfHarvest: 0.15, fallKrDefault: 1600, slaktKg: 60  },
  ],
  kronhjort: [
    { key: 'kron_kapital',  label: 'Kapitalhjort',          pctOfHarvest: 0.18, fallKrDefault: 8000, slaktKg: 115 },
    { key: 'kron_u10',      label: 'Hjort under 10 taggar', pctOfHarvest: 0.32, fallKrDefault: 5000, slaktKg: 70  },
    { key: 'kron_spets',    label: 'Spets',                 pctOfHarvest: 0.20, fallKrDefault: 3000, slaktKg: 48  },
    { key: 'kron_hind',     label: 'Hind',                  pctOfHarvest: 0.20, fallKrDefault: 2800, slaktKg: 55  },
    { key: 'kron_kalv',     label: 'Kalv',                  pctOfHarvest: 0.10, fallKrDefault: 1800, slaktKg: 32  },
  ],
  dovhjort: [
    { key: 'dov_spets',      label: 'Dovspets',      pctOfHarvest: 0.12, fallKrDefault: 5500, slaktKg: 58 },
    { key: 'dov_helskovel',  label: 'Dovhelskovel',  pctOfHarvest: 0.12, fallKrDefault: 4200, slaktKg: 50 },
    { key: 'dov_halvskovel', label: 'Dovhalvskovel', pctOfHarvest: 0.16, fallKrDefault: 2800, slaktKg: 38 },
    { key: 'dov_hind',       label: 'Dovhind',       pctOfHarvest: 0.35, fallKrDefault: 1600, slaktKg: 40 },
    { key: 'dov_kalv',       label: 'Dovkalv',       pctOfHarvest: 0.25, fallKrDefault: 900,  slaktKg: 18 },
  ],
  radjur: [
    { key: 'ra_medalj',  label: 'Råbock medalj',    pctOfHarvest: 0.08, fallKrDefault: 1400, slaktKg: 14 },
    { key: 'ra_6tag',    label: 'Råbock 6-taggare', pctOfHarvest: 0.12, fallKrDefault: 950,  slaktKg: 12 },
    { key: 'ra_4tag',    label: 'Råbock 4-taggare', pctOfHarvest: 0.12, fallKrDefault: 700,  slaktKg: 11 },
    { key: 'ra_spets',   label: 'Råbock spets',     pctOfHarvest: 0.08, fallKrDefault: 450,  slaktKg: 9  },
    { key: 'ra_get',     label: 'Råget',            pctOfHarvest: 0.35, fallKrDefault: 500,  slaktKg: 11 },
    { key: 'ra_kid',     label: 'Råkid',            pctOfHarvest: 0.25, fallKrDefault: 280,  slaktKg: 7  },
  ],
  vildsvin: [
    { key: 'vds_sugga',   label: 'Sugga',   pctOfHarvest: 0.30, fallKrDefault: 600, slaktKg: 50 },
    { key: 'vds_galt',    label: 'Galt',    pctOfHarvest: 0.28, fallKrDefault: 700, slaktKg: 65 },
    { key: 'vds_arsgris', label: 'Årsgris', pctOfHarvest: 0.42, fallKrDefault: 300, slaktKg: 22 },
  ],
  mufflon: [
    { key: 'muf_kapital', label: 'Kapital Bagge', pctOfHarvest: 0.20, fallKrDefault: 3500, slaktKg: 40 },
    { key: 'muf_bagge',   label: 'Mindre bagge',  pctOfHarvest: 0.25, fallKrDefault: 2000, slaktKg: 28 },
    { key: 'muf_hona',    label: 'Hona',          pctOfHarvest: 0.35, fallKrDefault: 1200, slaktKg: 22 },
    { key: 'muf_lamm',    label: 'Lamm',          pctOfHarvest: 0.20, fallKrDefault: 700,  slaktKg: 12 },
  ],
};

export const FAMILY_DISPLAY_NAMES: Record<string, string> = {
  alg: 'Älg', kronhjort: 'Kronhjort', dovhjort: 'Dovhjort',
  vildsvin: 'Vildsvin', radjur: 'Rådjur', bjorn: 'Björn',
  hare: 'Hare', fasan: 'Fasan', orre: 'Orre', tjader: 'Tjäder',
  skogsfagel: 'Skogsfågel', kanadagas: 'Kanadagås',
  andfagel: 'Andfågel', rav: 'Räv', gravling: 'Grävling',
  baver: 'Bäver', mink: 'Mink', mardhund: 'Mårdhund',
  mufflon: 'Mufflon',
};

/** familyKey → subcat display labels (för skjutmeny när inga planrader finns) */
export const SUBCAT_MAP: Partial<Record<string, string[]>> = Object.fromEntries(
  (Object.entries(SUBCATS) as [string, SubcatDef[]][]).map(([k, subs]) => [k, subs.map(s => s.label)])
) as Partial<Record<string, string[]>>;

/** Canonical display label for a family key */
export function familyLabel(key: string): string {
  return FAMILY_DISPLAY_NAMES[key] ?? key;
}
